import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { UserCog, UserPlus, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const load = async () => {
    const { data } = await api.get("/team");
    setMembers(data);
  };
  useEffect(() => { load(); }, []);

  const onInvite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/team/invite", form);
      setCreated(data);
      setForm({ full_name: "", email: "" });
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const isOwner = user?.role === "owner";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-3xl font-bold">Team</h1>
        {isOwner && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCreated(null); }}>
            <DialogTrigger asChild>
              <Button className="h-12 px-4 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="invite-btn">
                <UserPlus className="w-5 h-5 mr-1.5" />
                <span className="hidden sm:inline">Invite Technician</span>
                <span className="sm:hidden">Invite</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader>
                <DialogTitle>{created ? "Technician created" : "Invite a technician"}</DialogTitle>
              </DialogHeader>
              {!created ? (
                <form onSubmit={onInvite} className="space-y-4">
                  <div>
                    <Label className="text-sm font-semibold">Full Name</Label>
                    <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="h-12 mt-1.5" data-testid="invite-name" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">Email</Label>
                    <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 mt-1.5" data-testid="invite-email" />
                  </div>
                  <Button type="submit" disabled={saving} className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="invite-submit">
                    {saving ? "Creating…" : "Create technician"}
                  </Button>
                </form>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">{created.message}</p>
                  <CredRow label="Email" value={created.user.email} onCopy={() => copy(created.user.email)} testId="created-email" />
                  <CredRow label="Temporary password" value={created.temp_password} onCopy={() => copy(created.temp_password)} testId="created-password" />
                  <Button onClick={() => { setCreated(null); setOpen(false); }} className="w-full h-12 bg-slate-900 hover:bg-slate-800 font-semibold rounded-xl" data-testid="invite-done">
                    Done
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-2">
        {members.map((m) => (
          <div key={m.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`team-row-${m.id}`}>
            <div className="h-11 w-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold flex-shrink-0">
              {m.full_name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 truncate">{m.full_name}</div>
              <div className="text-sm text-slate-500 truncate">{m.email}</div>
            </div>
            <span className={`text-xs font-bold rounded-full px-2.5 py-1 ring-1 ring-inset ${m.role === "owner" ? "bg-blue-100 text-blue-800 ring-blue-200" : "bg-slate-100 text-slate-700 ring-slate-200"}`}>
              {m.role === "owner" ? "Owner" : "Technician"}
            </span>
          </div>
        ))}
        {!isOwner && (
          <p className="text-sm text-slate-500 mt-2 flex items-center gap-1"><UserCog className="w-4 h-4" /> Only owners can invite technicians.</p>
        )}
      </div>
    </div>
  );
}

function CredRow({ label, value, onCopy, testId }) {
  return (
    <div className="border border-slate-200 rounded-xl p-3">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="flex items-center justify-between gap-2 mt-1">
        <code className="font-mono text-sm bg-slate-50 px-2 py-1 rounded truncate flex-1" data-testid={testId}>{value}</code>
        <button onClick={onCopy} className="p-2 rounded-lg text-slate-600 hover:bg-slate-100" data-testid={`${testId}-copy`}>
          <Copy className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
